import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plug, PlugZap, Plus, Trash2 } from 'lucide-react';
import type { McpConnection } from '../../generated/api/types.gen.js';
import {
  connectMcpConnectionMutation,
  deleteMcpConnectionMutation,
  disconnectMcpConnectionMutation,
  listMcpConnectionsOptions,
  listMcpConnectionsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import {
  Badge,
  Button,
  Callout,
  ConfirmDialog,
  DataTable,
  EmptyState,
  SearchBar,
  Toolbar,
  useToast,
} from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { ListPage } from '../../components/page/ListPage.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { McpConnectionModal } from './McpConnectionModal.js';
import { McpTrustCell } from './McpTrustCell.js';

const TRANSPORT_LABEL: Record<McpConnection['transport'], string> = {
  stdio: 'Local process',
  http: 'HTTP',
  sse: 'Server-sent events',
};

export const McpConnectionsPage: React.FC = () => {
  const help = PAGE_HELP['mcp-connections'];
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState<McpConnection | null>(null);
  const [search, setSearch] = useState('');

  const { data, isPending, isError, refetch } = useQuery(listMcpConnectionsOptions());
  const refresh = () => queryClient.invalidateQueries({ queryKey: listMcpConnectionsQueryKey() });

  const connect = useMutation({
    ...connectMcpConnectionMutation(),
    onSuccess: refresh,
    onError: () => notify('Could not connect', 'danger'),
  });
  const disconnect = useMutation({
    ...disconnectMcpConnectionMutation(),
    onSuccess: refresh,
    onError: () => notify('Could not disconnect', 'danger'),
  });
  const remove = useMutation({
    ...deleteMcpConnectionMutation(),
    onSuccess: async () => {
      setDeleting(null);
      notify('Connection deleted');
      await refresh();
    },
    onError: () => notify('Could not delete the connection', 'danger'),
  });

  const needle = search.trim().toLowerCase();
  const connections = (data?.data ?? []).filter(
    (conn) => !needle || conn.name.toLowerCase().includes(needle) || conn.description?.toLowerCase().includes(needle),
  );

  const addButton = (
    <Can policy="CanManageIntegrations">
      <Button onClick={() => setShowCreate(true)}>
        <Plus size={14} aria-hidden="true" />
        Add connection
      </Button>
    </Can>
  );

  const columns: DataTableColumn<McpConnection>[] = [
    {
      key: 'name',
      header: 'Connection',
      sortable: true,
      sortValue: (conn) => conn.name.toLowerCase(),
      render: (conn) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-heading">{conn.name}</span>
          {conn.description && <span className="block truncate text-xs text-muted">{conn.description}</span>}
          {conn.lastError && <span className="block truncate text-xs text-danger">{conn.lastError}</span>}
        </div>
      ),
    },
    {
      key: 'transport',
      header: 'Transport',
      render: (conn) => <span className="text-sm text-body">{TRANSPORT_LABEL[conn.transport]}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (conn) => (
        <div className="flex flex-col items-start gap-1">
          <Badge variant={conn.connected ? 'success' : 'neutral'} dot>
            {conn.connected ? 'Connected' : 'Disconnected'}
          </Badge>
          {!conn.enabled && <Badge variant="warning">Disabled</Badge>}
        </div>
      ),
    },
    {
      key: 'tools',
      header: 'Tools',
      align: 'right',
      sortable: true,
      sortValue: (conn) => conn.toolCount ?? 0,
      render: (conn) => <span className="text-sm text-body">{conn.toolCount ?? 0}</span>,
    },
    { key: 'trust', header: 'Trust', render: (conn) => <McpTrustCell connection={conn} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '190px',
      render: (conn) => (
        <Can policy="CanManageIntegrations">
          <div className="flex items-center justify-end gap-1.5">
            {conn.connected ? (
              <Button
                size="sm"
                variant="secondary"
                loading={disconnect.isPending && disconnect.variables?.path.id === String(conn.id)}
                onClick={() => disconnect.mutate({ path: { id: String(conn.id) } })}
              >
                <Plug size={14} aria-hidden="true" />
                Disconnect
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                loading={connect.isPending && connect.variables?.path.id === String(conn.id)}
                onClick={() => connect.mutate({ path: { id: String(conn.id) } })}
              >
                <PlugZap size={14} aria-hidden="true" />
                Connect
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              aria-label={`Delete ${conn.name}`}
              onClick={() => setDeleting(conn)}
            >
              <Trash2 size={14} aria-hidden="true" />
            </Button>
          </div>
        </Can>
      ),
    },
  ];

  return (
    <ListPage
      title="MCP connections"
      subtitle="External tool servers that agents can call."
      helpContent={help}
      actions={addButton}
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load connections"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={<Toolbar search={<SearchBar value={search} onChange={setSearch} placeholder="Search connections…" />} />}
    >
      {!isError && (
        <DataTable
          columns={columns}
          rows={connections}
          rowKey={(conn) => conn.id}
          loading={isPending}
          aria-label="MCP connections"
          empty={
            <EmptyState
              icon={Plug}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
              action={addButton}
            />
          }
        />
      )}

      <McpConnectionModal open={showCreate} onClose={() => setShowCreate(false)} />
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate({ path: { id: String(deleting.id) } })}
        title="Delete connection"
        message={`Delete "${deleting?.name ?? ''}"? Agents lose the tools it provides.`}
        confirmLabel="Delete connection"
        loading={remove.isPending}
      />
    </ListPage>
  );
};
